import React, {
  memo, useState, useEffect, useMemo,
} from 'react';
import { Form, Input } from 'formik-antd';
import {
  FormikProvider, FieldArray, ArrayHelpers, FormikContextType,
} from 'formik';
import { EnvironmentOutlined, EnvironmentFilled } from '@ant-design/icons';
import { ReactComponent as ThrashCan } from '../../../assets/icons/thrashCan.svg';
import FieldArrayRef from '../../../utils/FieldArrayRef';
import {
  IZoneFormValues, IDrawnPolygons,
} from './ZoneMaker.types';

interface IZoneFormErrors {
  zones?: Array<{ name?: string }> | string;
}

interface IFormProps {
  formik: FormikContextType<IZoneFormValues> & { errors: IZoneFormErrors };
  // eslint-disable-next-line
  fieldArrayRef: React.RefObject<any>;
  isDrawing: boolean;
  activeZoneIndex: number | null;
  drawnPolygons: IDrawnPolygons;
  startDrawing: (index: number) => void;
  deleteZonePolygon: (index: number) => void;
  cancelDrawing: () => void;
  setDeleteModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setDeleteTarget: React.Dispatch<React.SetStateAction<number | null>>;
  editingZoneIndex: number | null;
  startEditing: (index: number) => void;
  cancelEditing: (index: number) => void;
  saveEditing: (index: number) => void;
  handlePolygonColorChange: (index: number) => void;
  currentZonesFilter: string;
  searchZonesValue: string;
  startInsertingRectangle: (index: number) => void;
  isInsertingRectangle: boolean;
  cancelInsertingRectangle: (index: number) => void;
  saveInsertingRectangle: (index: number) => void;
}

const FormComponent: React.FC<IFormProps> = ({
  formik,
  fieldArrayRef,
  isDrawing,
  activeZoneIndex,
  drawnPolygons,
  startDrawing,
  deleteZonePolygon,
  cancelDrawing,
  setDeleteModalVisible,
  setDeleteTarget,
  editingZoneIndex,
  startEditing,
  cancelEditing,
  saveEditing,
  handlePolygonColorChange,
  currentZonesFilter,
  searchZonesValue,
  startInsertingRectangle,
  isInsertingRectangle,
  cancelInsertingRectangle,
  saveInsertingRectangle,
}) => {
  const { errors, values } = formik;

  const isAnyZoneEditing = editingZoneIndex !== null;
  const disabled = isDrawing || isAnyZoneEditing || isInsertingRectangle;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const filteredZonesWithIndex = useMemo(() => (values.zones ? values.zones
    .map((zone, index) => ({
      ...zone, originalIndex: index,
    }))
    .filter((zone) => {
      if (!currentZonesFilter) return true;
      const key = currentZonesFilter.toLowerCase();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const value = (zone as any)[key];
      return value?.toLowerCase().includes(searchZonesValue.toLowerCase());
    }) : []), [values.zones, currentZonesFilter, searchZonesValue]);

  useEffect(() => {
    setSelectedIndex(null);
  }, [editingZoneIndex]);

  return (
    <div className="zone-maker-form">
      <div>
        <span>#</span>
        <span style={{ paddingLeft: '15px' }}>Zone</span>
      </div>
      <FormikProvider value={formik}>
        <Form>
          <FieldArray name="zones">
            {(arrayHelpers: ArrayHelpers) => (
              <FieldArrayRef
                ref={fieldArrayRef}
                fieldArrayHelpers={arrayHelpers}
                dirty={formik.dirty}
                values={values}
              >
                {filteredZonesWithIndex.length > 0 && (
                  <div className="zone-maker-inputs">
                    {filteredZonesWithIndex.map(({ originalIndex, ...zone }, mapIndex) => {
                      const index = originalIndex;
                      const zoneError = errors?.zones?.[index] as { name?: string }
                      | undefined;
                      const hasPolygon = !!drawnPolygons[index]?.polygon;
                      const isEditMode = !isInsertingRectangle && editingZoneIndex === index;
                      const isInsertMode = isInsertingRectangle && editingZoneIndex === index;
                      return (
                        <div key={mapIndex} className="zone-maker-input-wrapper">
                          <div className="zone-maker-input-row">
                            <div className="zone-maker-index">
                              {index + 1}
                            </div>
                            { (selectedIndex !== index) ? (
                              <EnvironmentOutlined
                                style={{
                                  color: hasPolygon ? '#3d1cb0' : '#ccc',
                                  cursor: hasPolygon ? 'pointer' : 'not-allowed',
                                }}
                                onClick={() => {
                                  handlePolygonColorChange(index);
                                  setSelectedIndex(index);
                                }}
                              />
                            ) : (
                              <EnvironmentFilled
                                style={{
                                  color: hasPolygon ? '#3d1cb0' : '#ccc',
                                  cursor: hasPolygon ? 'pointer' : 'not-allowed',
                                }}
                                onClick={() => {
                                  handlePolygonColorChange(index);
                                  setSelectedIndex(null);
                                }}
                              />
                            )}
                            <div className="zone-maker-input-wrapper">
                              <Input
                                name={`zones.${index}.name`}
                                placeholder="Zone name"
                                className="zone-maker-input"
                                autoComplete="off"
                                onChange={(e) => {
                                  formik.setFieldValue(`zones.${index}.name`, e.target.value);
                                  formik.setFieldTouched(`zones.${index}.name`, true, false);
                                }}
                              />
                              {formik.touched.zones?.[index]?.name && zoneError?.name && (
                                <span className="zone-maker-error">{zoneError.name}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              className="zone-maker-delete"
                              onClick={() => {
                                setDeleteTarget(index);
                                setDeleteModalVisible(true);
                              }}
                            >
                              <ThrashCan />
                            </button>
                          </div>
                          <div className="zone-maker-input-row">
                            <button
                              type="button"
                              onClick={() => startDrawing(index)}
                              disabled={disabled}
                              className="zone-maker-draw-button"
                              style={{
                                cursor: disabled ? 'not-allowed' : 'pointer',
                              }}
                            >
                              Draw
                            </button>
                            { !isInsertMode ? (
                              <button
                                type="button"
                                onClick={() => startInsertingRectangle(index)}
                                disabled={disabled}
                                className="zone-maker-draw-button"
                                style={{
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Insert
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="zone-maker-edit-button"
                                onClick={() => saveInsertingRectangle(index)}
                              >
                                Done
                              </button>
                            )}
                            {
                              hasPolygon && (
                                <>
                                  { !isEditMode ? (
                                    <button
                                      type="button"
                                      className="zone-maker-edit-button"
                                      disabled={disabled}
                                      onClick={() => startEditing(index)}
                                      style={{
                                        cursor: disabled ? 'not-allowed' : 'pointer',
                                      }}
                                    >
                                      Edit
                                    </button>
                                  )
                                    : (
                                      <button
                                        type="button"
                                        className="zone-maker-edit-button"
                                        onClick={() => saveEditing(index)}
                                      >
                                        Done
                                      </button>
                                    )}
                                </>
                              )
                            }
                            {hasPolygon && (
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => {
                                  deleteZonePolygon(index);
                                  setSelectedIndex(null);
                                }}
                                className="zone-maker-clear-button"
                                style={{
                                  cursor: disabled ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Clear
                              </button>
                            )}
                          </div>
                          {isDrawing && (activeZoneIndex === index) && (
                            <div>
                              <div className="zone-maker-overlay-box">
                                <div>
                                  Drawing Zone
                                  {activeZoneIndex !== null ? ` ${activeZoneIndex + 1}` : ''}
                                </div>
                                <div style={{ padding: '10px 0' }}>
                                  Click on image to add points.
                                  In the end click first point to close polygon.
                                  Click ESC to undo last change.
                                </div>
                                <button
                                  type="button"
                                  onClick={cancelDrawing}
                                  className="zone-maker-cancel-button"
                                >
                                  Cancel Drawing
                                </button>
                              </div>
                            </div>
                          )}
                          {isInsertingRectangle && (activeZoneIndex === index) && (
                            <div>
                              <div className="zone-maker-overlay-box">
                                <div>
                                  Inserting rectangle for Zone
                                  {activeZoneIndex !== null ? ` ${activeZoneIndex + 1}` : ''}
                                </div>
                                <div style={{ padding: '10px 0' }}>
                                  Drag polygon to move it.
                                  Drag polygon vertex to re-shape polygon.
                                  When finished click Done.
                                </div>
                                <button
                                  type="button"
                                  onClick={() => cancelInsertingRectangle(index)}
                                  className="zone-maker-cancel-button"
                                >
                                  Cancel Inserting
                                </button>
                              </div>
                            </div>
                          )}
                          {isEditMode && (
                            <div>
                              <div className="zone-maker-overlay-box">
                                <div>
                                  Editing Zone
                                  { editingZoneIndex !== null ? ` ${editingZoneIndex + 1}` : ''}
                                </div>
                                <div style={{ padding: '10px 0' }}>
                                  Drag polygon to move it.
                                  Drag polygon vertex to re-shape polygon.
                                  When finished click Done.
                                </div>
                                <button
                                  type="button"
                                  onClick={() => cancelEditing(index)}
                                  className="zone-maker-cancel-button"
                                >
                                  Cancel Editing
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </FieldArrayRef>
            )}
          </FieldArray>
        </Form>
      </FormikProvider>
    </div>
  );
};

export default memo(FormComponent);
